import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getExperience, updateExperience } from 'lib/API/experience';

// ── Inline helpers for the few structural transforms ──

/** Skills: keep the full skill objects the picker/API use, unchanged. */
const skillsFromApi = (skills) => (Array.isArray(skills) ? skills : []);

/**
 * Skills → API array. Every skill must carry { id, name, type } or the backend
 * rejects with 422; `type` defaults to 'skill'. Extra fields (e.g. rating) are
 * dropped so both pages send an identical shape.
 */
const skillsToApi = (skills) =>
  (Array.isArray(skills) ? skills : [])
    .filter((s) => s && s.name)
    .map((s) => ({ id: s.id || '', name: s.name, type: s.type || 'skill' }));

/** currently_enrolled: API boolean → select string */
const boolToSelectStr = (val) => (val === true ? 'true' : val === false ? 'false' : '');

/** currently_enrolled: select string → API boolean */
const selectStrToBool = (val) => (val === 'true' ? true : val === 'false' ? false : undefined);

/** currently_enrolled: API boolean → checkbox 'yes'/'no' string */
const boolToYesNo = (val) => (val === true ? 'yes' : val === false ? 'no' : '');

/** currently_enrolled: checkbox 'yes'/'no' string → API boolean */
const yesNoToBool = (val) => (val === 'yes' ? true : val === 'no' ? false : undefined);

/** institution: API nested object → flat fields with institution_ prefix */
const flattenInstitution = (record) => {
  const inst = record.institution || {};
  return {
    institution_name: inst.name || '',
    institution_address: inst.address || '',
    institution_city: inst.city || '',
    institution_state: inst.state || '',
    institution_zipcode: inst.zipcode || '',
  };
};

/** institution: flat fields → API nested object */
const nestInstitution = (record) => ({
  name: record.institution_name || '',
  address: record.institution_address || '',
  city: record.institution_city || '',
  state: record.institution_state || '',
  zipcode: record.institution_zipcode || '',
});

const generateId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Normalize branch values: old JST uploads stored camelCase ('airNationalGuard'),
// backend enum now requires display names ('Air National Guard').
const JST_BRANCH_DISPLAY = {
  airForce: 'Air Force',
  airNationalGuard: 'Air National Guard',
  army: 'Army',
  armyNationalGuard: 'Army National Guard',
  coastGuard: 'Coast Guard',
  marineCorps: 'Marine Corps',
  navy: 'Navy',
  spaceForce: 'Space Force',
};
const normalizeJstBranch = (val) => JST_BRANCH_DISPLAY[val] || val || '';

// ── Transform API response → Redux state ──

const mapApiToSections = (jst) => {
  if (!jst) return {};
  const sections = {};

  if (jst.personal) {
    const { currently_enlisted, personnel_category, ...personalRest } = jst.personal; //eslint-disable-line
    sections.personal = [
      {
        id: generateId('pi'),
        ...personalRest,
      },
    ];
  }
  if (Array.isArray(jst.military_courses)) {
    sections.military_courses = jst.military_courses.map((c) => {
      const { credit_area, parsed_skills, currently_enrolled, ...rest } = c;
      return {
        id: generateId('mc'),
        ...rest,
        parsed_skills: skillsFromApi(parsed_skills),
        currently_enrolled: boolToYesNo(currently_enrolled),
        credit_area: (Array.isArray(credit_area) ? credit_area : []).map((ca) => ({ id: generateId('ca'), ...ca })),
      };
    });
  }
  if (Array.isArray(jst.military_experience)) {
    sections.military_experience = jst.military_experience.map((e) => {
      // Extract 'id' (SOC code from career recommender) separately so stripId()
      // doesn't discard it — it's stored as soc_id and re-attached on PUT.
      const { id: socId, credit_area, parsed_skills, currently_enrolled, branch_of_service, ...rest } = e;
      const mocFromTitle = rest.moc_code || (rest.experience_title || '').match(/\(([^)]+)\)$/)?.[1] || '';
      return {
        id: generateId('me'),
        soc_id: socId || null,
        ...rest,
        moc_code: mocFromTitle,
        branch_of_service: normalizeJstBranch(branch_of_service),
        parsed_skills: skillsFromApi(parsed_skills),
        currently_enrolled: boolToYesNo(currently_enrolled),
        credit_area: (Array.isArray(credit_area) ? credit_area : []).map((ca) => ({ id: generateId('ca'), ...ca })),
      };
    });
  }
  if (Array.isArray(jst.clep_and_dsst_test_scores)) {
    sections.clep_and_dsst_test_scores = jst.clep_and_dsst_test_scores.map((t) => ({
      id: generateId('cd'),
      ...t,
    }));
  }
  if (Array.isArray(jst.dlpt_test_scores)) {
    sections.dlpt_test_scores = jst.dlpt_test_scores.map((t) => ({
      id: generateId('dl'),
      ...t,
    }));
  }
  if (Array.isArray(jst.other_learning)) {
    sections.other_learning = jst.other_learning.map((o) => {
      const { parsed_skills, ...rest } = o;
      return {
        id: generateId('ol'),
        ...rest,
        parsed_skills: skillsFromApi(parsed_skills),
      };
    });
  }
  if (Array.isArray(jst.academic_courses)) {
    sections.academic_courses = jst.academic_courses.map((c) => {
      const { parsed_skills, currently_enrolled, ...rest } = c;
      return {
        id: generateId('ac'),
        ...rest,
        ...flattenInstitution(c),
        parsed_skills: skillsFromApi(parsed_skills),
        currently_enrolled: boolToSelectStr(currently_enrolled),
      };
    });
  }
  if (Array.isArray(jst.academic_degrees_certificates)) {
    sections.academic_degrees_certificates = jst.academic_degrees_certificates.map((d) => {
      const { skills_gained, currently_enrolled, ...rest } = d;
      return {
        id: generateId('ad'),
        ...rest,
        skills_gained: skillsFromApi(skills_gained),
        currently_enrolled: boolToSelectStr(currently_enrolled),
      };
    });
  }

  return sections;
};

// ── Transform Redux state → API payload ──

const stripId = ({ id, ...rest }) => rest;

/** Convert empty date strings to null for the API */
const emptyToNull = (val) => (val === '' || val == null ? null : val);

const mapSectionsToApi = (sections) => {
  const personal =
    Array.isArray(sections.personal) && sections.personal.length > 0
      ? (() => {
          const { start_date, end_date, personnel_category, ...rest } = stripId(sections.personal[0]); //eslint-disable-line
          return {
            ...rest,
            start_date: emptyToNull(start_date),
            end_date: rest.status === 'active' ? null : emptyToNull(end_date),
          };
        })()
      : undefined;

  return {
    personal,
    military_courses: (sections.military_courses || []).map((r) => {
      const { parsed_skills, credit_area, start_date, end_date, currently_enrolled, ...rest } = stripId(r);
      const isEnrolled = currently_enrolled === 'yes';
      return {
        ...rest,
        start_date: emptyToNull(start_date),
        end_date: isEnrolled ? null : emptyToNull(end_date),
        currently_enrolled: isEnrolled ? true : false,
        parsed_skills: skillsToApi(parsed_skills),
        credit_area: Array.isArray(credit_area) && credit_area.length > 0 ? credit_area.map(stripId) : undefined,
      };
    }),
    military_experience: (sections.military_experience || []).map((r) => {
      // soc_id must be extracted before stripId so it isn't lost with the UI 'id'.
      const { soc_id, ...withoutSocId } = r;
      const { parsed_skills, credit_area, currently_enrolled, end_date, ...rest } = stripId(withoutSocId);
      const isEnrolled = currently_enrolled === 'yes';
      return {
        ...rest,
        ...(soc_id ? { id: soc_id } : {}),
        end_date: isEnrolled ? null : emptyToNull(end_date),
        currently_enrolled: isEnrolled ? true : null,
        parsed_skills: skillsToApi(parsed_skills),
        credit_area: Array.isArray(credit_area) && credit_area.length > 0 ? credit_area.map(stripId) : undefined,
      };
    }),
    clep_and_dsst_test_scores: (sections.clep_and_dsst_test_scores || []).map((r) => {
      const rec = stripId(r);
      return { ...rec, date_taken: emptyToNull(rec.date_taken) };
    }),
    dlpt_test_scores: (sections.dlpt_test_scores || []).map((r) => {
      const rec = stripId(r);
      return { ...rec, date_taken: emptyToNull(rec.date_taken) };
    }),
    other_learning: (sections.other_learning || []).map((r) => {
      const { parsed_skills, reason_no_credit_recommendation, ...rest } = stripId(r);
      return {
        ...rest,
        date_taken: emptyToNull(rest.date_taken),
        parsed_skills: skillsToApi(parsed_skills),
        reason_no_credit_recommendation: emptyToNull(reason_no_credit_recommendation),
      };
    }),
    academic_courses: (sections.academic_courses || []).map((r) => {
      const { parsed_skills, currently_enrolled, end_date, grade, credits, level, ...rest } = stripId(r);
      const isEnrolled = currently_enrolled === 'true';
      return {
        ...rest,
        end_date: isEnrolled ? null : emptyToNull(end_date),
        grade: isEnrolled ? null : emptyToNull(grade),
        credits: emptyToNull(credits),
        level: emptyToNull(level),
        institution: nestInstitution(r),
        parsed_skills: skillsToApi(parsed_skills),
        currently_enrolled: selectStrToBool(currently_enrolled),
      };
    }),
    academic_degrees_certificates: (sections.academic_degrees_certificates || []).map((r) => {
      const { skills_gained, currently_enrolled, end_date, ...rest } = stripId(r);
      return {
        ...rest,
        end_date: currently_enrolled === 'true' ? null : emptyToNull(end_date),
        skills_gained: skillsToApi(skills_gained),
        currently_enrolled: selectStrToBool(currently_enrolled),
      };
    }),
  };
};

// ── Async thunks ──

/** GET /users/me/experience → populate JST sections from API */
export const fetchJstData = createAsyncThunk('jst/fetchJstData', async (_, { rejectWithValue }) => {
  try {
    const experience = await getExperience();
    return mapApiToSections(experience?.jst);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to fetch JST data');
  }
});

/** PUT /users/me/experience with transformed JST payload */
export const saveJstData = createAsyncThunk('jst/saveJstData', async (_, { getState, rejectWithValue }) => {
  try {
    const { jst } = getState();
    const jstPayload = mapSectionsToApi(jst.sections);
    await updateExperience({ jst: jstPayload });
    return true;
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to save JST data');
  }
});

// ── Slice ──

const initialState = {
  sections: {
    personal: [],
    military_courses: [],
    military_experience: [],
    clep_and_dsst_test_scores: [],
    dlpt_test_scores: [],
    other_learning: [],
    academic_courses: [],
    academic_degrees_certificates: [],
  },
  activeForm: null, // which section's form is currently open
  editingRecord: null,
  isDirty: false, // true when there are unsaved changes
  personalSaved: false, // gate: true once personal info has been persisted
  loading: false, // API loading state
  saving: false, // API saving state
  error: null, // last error message
};

export const jstSlice = createSlice({
  name: 'jst',
  initialState,
  reducers: {
    addRecord: (state, action) => {
      const { sectionId, record } = action.payload;
      if (state.sections[sectionId]) {
        state.sections[sectionId].push(record);
        state.isDirty = true;
      }
    },
    updateRecord: (state, action) => {
      const { sectionId, recordId, updates } = action.payload;
      const records = state.sections[sectionId];
      if (records) {
        const index = records.findIndex((r) => r.id === recordId);
        if (index !== -1) {
          records[index] = { ...records[index], ...updates };
          state.isDirty = true;
        }
      }
    },
    deleteRecord: (state, action) => {
      const { sectionId, recordId } = action.payload;
      if (state.sections[sectionId]) {
        state.sections[sectionId] = state.sections[sectionId].filter((r) => r.id !== recordId);
        state.isDirty = true;
      }
    },
    setActiveForm: (state, action) => {
      state.activeForm = action.payload;
      if (action.payload === null) {
        state.editingRecord = null;
      }
    },
    setEditingRecord: (state, action) => {
      state.editingRecord = action.payload;
      if (action.payload) {
        state.activeForm = action.payload.sectionId;
      }
    },
    loadSectionData: (state, action) => {
      const { sectionId, data } = action.payload;
      state.sections[sectionId] = data;
    },
    markSaved: (state) => {
      state.isDirty = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchJstData
    builder
      .addCase(fetchJstData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJstData.fulfilled, (state, action) => {
        state.loading = false;
        const mapped = action.payload;
        Object.keys(mapped).forEach((sectionId) => {
          if (state.sections[sectionId] !== undefined) {
            state.sections[sectionId] = mapped[sectionId];
          }
        });
        state.isDirty = false;
        // If personal section already has data from API, mark gate as open
        state.personalSaved = Array.isArray(mapped.personal) && mapped.personal.length > 0;
      })
      .addCase(fetchJstData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch JST data';
      });

    // saveJstData
    builder
      .addCase(saveJstData.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveJstData.fulfilled, (state) => {
        state.saving = false;
        state.isDirty = false;
        // Once saved successfully and personal section has data, open the gate
        if (state.sections.personal && state.sections.personal.length > 0) {
          state.personalSaved = true;
        }
      })
      .addCase(saveJstData.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to save JST data';
      });
  },
});

export const {
  addRecord,
  updateRecord,
  deleteRecord,
  setActiveForm,
  setEditingRecord,
  loadSectionData,
  markSaved,
  clearError,
} = jstSlice.actions;

export default jstSlice.reducer;
